import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, ImageIcon } from "lucide-react";
import { authedAxios, formatApiErrorDetail } from "./api.js";
import { mediaUrl } from "@/content/ContentContext.jsx";

export const FieldLabel = ({ children }) => (
  <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-brand">{children}</label>
);

export const TextInput = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <input
      className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-brand focus:ring-4 focus:ring-brand/10"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
  </div>
);

export const TextArea = ({ label, value, onChange, rows = 3, testid }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <textarea
      rows={rows}
      className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-brand focus:ring-4 focus:ring-brand/10"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
  </div>
);

export const ImageInput = ({ label, value, onChange, testid }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await authedAxios.post("/admin/upload", fd);
      onChange(data.path);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex flex-wrap items-start gap-4 rounded-xl border border-zinc-200 bg-white p-4">
        <div className="h-24 w-32 shrink-0 overflow-hidden rounded-lg bg-zinc-100">
          {value ? (
            <img src={mediaUrl(value)} alt="preview" className="h-full w-full object-cover" data-testid={`${testid}-preview`} />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-zinc-300">
              <ImageIcon size={22} />
            </div>
          )}
        </div>
        <div className="min-w-[200px] flex-1 space-y-2">
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            ref={fileRef}
            onChange={onFile}
            className="hidden"
            data-testid={`${testid}-file`}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-brand-deep disabled:opacity-60"
            data-testid={`${testid}-upload-btn`}
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading…" : "Upload Image"}
          </button>
          <input
            className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-500 outline-none focus:border-brand"
            value={value}
            placeholder="…or paste an image URL"
            onChange={(e) => onChange(e.target.value)}
            data-testid={`${testid}-url`}
          />
        </div>
      </div>
    </div>
  );
};

export const ListEditor = ({ label, items, onChange, newItem, renderItem, testid }) => (
  <div>
    <div className="mb-3 flex items-center justify-between">
      <FieldLabel>{label}</FieldLabel>
      <button
        type="button"
        onClick={() => onChange([...items, newItem()])}
        className="rounded-full border border-brand/30 px-4 py-1.5 text-xs font-semibold text-brand transition-colors hover:bg-brand hover:text-white"
        data-testid={`${testid}-add`}
      >
        + Add
      </button>
    </div>
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={`${testid}-item-${i}`}>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-zinc-400">#{i + 1}</span>
            <button
              type="button"
              onClick={() => onChange(items.filter((_, j) => j !== i))}
              className="text-xs font-semibold text-red-500 transition-colors hover:text-red-700"
              data-testid={`${testid}-remove-${i}`}
            >
              Remove
            </button>
          </div>
          {renderItem(item, (patch) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it))))}
        </div>
      ))}
    </div>
  </div>
);
